import {createContext, ReactNode, useContext, useMemo, useState} from 'react';
import {createTheme, ThemeProvider} from '@mui/material/styles';
import {enUS, plPL, ruRU, ukUA} from '@mui/material/locale';
import {useTranslation} from 'react-i18next';
import {AppThemeId, createAppTheme, DEFAULT_APP_THEME, isAppThemeId} from '@mybake/ui/theme';

const STORAGE_KEY = 'bakery-app-theme';
const materialLocales = {en: enUS, pl: plPL, ru: ruRU, uk: ukUA};

interface AppThemeContextValue {
    themeId: AppThemeId;
    setThemeId: (themeId: AppThemeId) => void;
}

const AppThemeContext = createContext<AppThemeContextValue | undefined>(undefined);

const getInitialTheme = (): AppThemeId => {
    const savedTheme = window.localStorage.getItem(STORAGE_KEY);
    return isAppThemeId(savedTheme) ? savedTheme : DEFAULT_APP_THEME;
};

export const AppThemeProvider = ({children}: {children: ReactNode}) => {
    const {i18n} = useTranslation();
    const language = (i18n.resolvedLanguage || i18n.language || 'pl').split('-')[0];
    const [themeId, setThemeIdState] = useState<AppThemeId>(getInitialTheme);
    const theme = useMemo(() => createTheme(createAppTheme(themeId),
        materialLocales[language as keyof typeof materialLocales] || plPL), [themeId, language]);

    const setThemeId = (nextThemeId: AppThemeId) => {
        window.localStorage.setItem(STORAGE_KEY, nextThemeId);
        setThemeIdState(nextThemeId);
    };

    const value = useMemo(() => ({themeId, setThemeId}), [themeId]);

    return (
        <AppThemeContext.Provider value={value}>
            <ThemeProvider theme={theme}>{children}</ThemeProvider>
        </AppThemeContext.Provider>
    );
};

export const useAppTheme = () => {
    const context = useContext(AppThemeContext);
    if (!context) throw new Error('useAppTheme must be used inside AppThemeProvider');
    return context;
};
